import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { AsyncSearchableSelect } from '../ui/async-searchable-select';
import { SearchableSelect } from '../ui/searchable-select';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { directoryApi } from '../../services/api';
import { useCanonicalCountryOptions } from '../../utils/countries';

const mapPortToOption = (p) => ({
  value: p.code,
  label: p.nameAndLocode || `${p.name} (${p.code})`,
  country: p.country,
});
const DIMENSION_UNIT_OPTIONS = [
  { value: 'mm', label: 'mm' },
  { value: 'cm', label: 'cm' },
  { value: 'inch', label: 'Inch' },
  { value: 'm', label: 'Meter' },
];
const RUNNING_CONDITION_OPTIONS = [
  { value: 'yes', label: 'Running/Operational' },
  { value: 'no', label: 'Non-Running' },
];

const DEFAULT_BREAKBULK_FORM = {
  cargoType: 'breakbulk',
  scope: [],
  isRoRo: false,
  origin: { port: '', country: '' },
  destination: { port: '', country: '' },
  commodity: '',
  cargoDescription: '',
  pieces: '',
  totalWeight: '',
  dimensions: { length: '', width: '', height: '', unit: 'm' },
  stowagePreference: 'on_deck',
  packaging: '',
  routeSurvey: false,
  photos: [],
  roRoDetails: { vehicleType: '', make: '', model: '', quantity: '', running: 'yes' },
  lashingRequired: false,
  specialHandling: [],
  insuranceRequired: false,
  insuranceValue: '',
  notes: ''
};

const BreakbulkForm = ({ onSubmit, onCancel, commodities = [], initialData = null, formId = null, scopeOptions: scopeOptionsProp = [], onFormDataChange, validationErrors = {}, clearValidationError }) => {
  const [formData, setFormData] = useState(() => {
    if (initialData && typeof initialData === 'object') {
      return { ...DEFAULT_BREAKBULK_FORM, ...initialData };
    }
    return { ...DEFAULT_BREAKBULK_FORM };
  });
  const [portLabels, setPortLabels] = useState({ origin: '', destination: '' });
  const countryOptions = useCanonicalCountryOptions();
  const commodityOptions = useMemo(() => (
    commodities.map((comm) => {
      const value = typeof comm === 'object' ? comm.name || String(comm) : comm;
      return { value, label: value };
    })
  ), [commodities]);
  const scopeOptions = scopeOptionsProp && scopeOptionsProp.length > 0 ? scopeOptionsProp : [];
  const stowageOptions = [
    { value: 'on_deck', label: 'On Deck' },
    { value: 'under_deck', label: 'Under Deck' },
    { value: 'weather_deck', label: 'Weather Deck' },
    { value: 'no_preference', label: 'No Preference' }
  ];

  const specialHandlingOptions = ['Lashing & Securing', 'Heavy Lift Equipment', 'Port Crane', 'Special Permits', 'Escort Required'];
  const vehicleTypes = ['Car', 'Truck', 'Bus', 'Construction Equipment', 'Agricultural Equipment', 'Other'];

  useEffect(() => {
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange]);

  useEffect(() => {
    const fetchLabel = async (field, code) => {
      if (!code) return;
      try {
        const res = await directoryApi.getPortByCode(code);
        const item = res.data;
        const label = item?.nameAndLocode || (item?.name && item?.code ? `${item.name} (${item.code})` : code);
        setPortLabels((prev) => ({ ...prev, [field]: label }));
      } catch (_) {
        setPortLabels((prev) => ({ ...prev, [field]: code }));
      }
    };
    if (formData.origin?.port) fetchLabel('origin', formData.origin.port);
    if (formData.destination?.port) fetchLabel('destination', formData.destination.port);
  }, [formData.origin?.port, formData.destination?.port]);

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData({ ...formData, photos: [...formData.photos, ...files] });
    toast.success(`${files.length} file(s) uploaded`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6" noValidate>
      {scopeOptions.length > 0 && (
        <div>
          <Label>Scope (Select all that apply)</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
            {scopeOptions.map((scope) => (
              <label key={scope} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={(formData.scope || []).includes(scope)}
                  onChange={() => {
                    const current = formData.scope || [];
                    const newScopes = current.includes(scope) ? current.filter(s => s !== scope) : [...current, scope];
                    setFormData({ ...formData, scope: newScopes });
                    onFormDataChange?.({ scope: newScopes });
                    clearValidationError?.('scope');
                  }}
                  className="rounded"
                />
                <span className="text-sm">{scope}</span>
              </label>
            ))}
          </div>
          {validationErrors.scope && <p className="mt-1 text-sm text-red-600">{validationErrors.scope}</p>}
        </div>
      )}
      {/* Cargo Type Selection */}
      <div>
        <Label>Cargo Type</Label>
        <div className="flex space-x-4 mt-2">
          <label className="flex items-center space-x-2 p-3 border rounded cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              checked={!formData.isRoRo}
              onChange={() => setFormData({ ...formData, isRoRo: false })}
            />
            <span className="font-medium">Breakbulk Cargo</span>
          </label>
          <label className="flex items-center space-x-2 p-3 border rounded cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              checked={formData.isRoRo}
              onChange={() => setFormData({ ...formData, isRoRo: true })}
            />
            <span className="font-medium">Ro-Ro (Roll-on/Roll-off)</span>
          </label>
        </div>
      </div>

      {/* Origin & Destination */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Route</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Origin Port</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchPorts(q, lim);
                return (res.data || []).map(mapPortToOption);
              }}
              value={formData.origin.port}
              selectedLabel={portLabels.origin}
              onChange={(value, item) => {
                setFormData({
                  ...formData,
                  origin: {
                    ...formData.origin,
                    port: value || '',
                    country: item?.country || ''
                  }
                });
                setPortLabels((prev) => ({ ...prev, origin: item?.label || value || '' }));
                if (value) clearValidationError?.('origin.port');
              }}
              placeholder="Select Port..."
              className="w-full"
              error={Boolean(validationErrors['origin.port'])}
            />
            {validationErrors['origin.port'] && <p className="mt-1 text-sm text-red-600">{validationErrors['origin.port']}</p>}
          </div>
          <div>
            <Label>Origin Country</Label>
            <SearchableSelect
              options={countryOptions}
              value={formData.origin.country}
              onChange={(value) => {
                setFormData({
                  ...formData,
                  origin: {
                    ...formData.origin,
                    country: value || ''
                  }
                });
                if (value) clearValidationError?.('origin.country');
              }}
              placeholder="Search or select country"
              allowOther={false}
              error={Boolean(validationErrors['origin.country'])}
            />
            {validationErrors['origin.country'] && <p className="mt-1 text-sm text-red-600">{validationErrors['origin.country']}</p>}
          </div>
          <div>
            <Label>Destination Port</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchPorts(q, lim);
                return (res.data || []).map(mapPortToOption);
              }}
              value={formData.destination.port}
              selectedLabel={portLabels.destination}
              onChange={(value, item) => {
                setFormData({
                  ...formData,
                  destination: {
                    ...formData.destination,
                    port: value || '',
                    country: item?.country || ''
                  }
                });
                setPortLabels((prev) => ({ ...prev, destination: item?.label || value || '' }));
                if (value) clearValidationError?.('destination.port');
              }}
              placeholder="Select Port..."
              className="w-full"
              error={Boolean(validationErrors['destination.port'])}
            />
            {validationErrors['destination.port'] && <p className="mt-1 text-sm text-red-600">{validationErrors['destination.port']}</p>}
          </div>
          <div>
            <Label>Destination Country</Label>
            <SearchableSelect
              options={countryOptions}
              value={formData.destination.country}
              onChange={(value) => {
                setFormData({
                  ...formData,
                  destination: {
                    ...formData.destination,
                    country: value || ''
                  }
                });
                if (value) clearValidationError?.('destination.country');
              }}
              placeholder="Search or select country"
              allowOther={false}
              error={Boolean(validationErrors['destination.country'])}
            />
            {validationErrors['destination.country'] && <p className="mt-1 text-sm text-red-600">{validationErrors['destination.country']}</p>}
          </div>
        </div>
      </div>

      {/* Ro-Ro Specific Fields */}
      {formData.isRoRo ? (
        <div className="border-t pt-4">
          <h3 className="font-semibold text-lg mb-4">Ro-Ro Vehicle Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Vehicle Type</Label>
              <SearchableSelect
                options={vehicleTypes.map((type) => ({ value: type, label: type }))}
                value={formData.roRoDetails.vehicleType}
                onChange={(value) => setFormData({ ...formData, roRoDetails: { ...formData.roRoDetails, vehicleType: value || '' } })}
                placeholder="Search or select vehicle type"
                allowOther={false}
              />
            </div>
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                value={formData.roRoDetails.quantity}
                onChange={(e) => setFormData({ ...formData, roRoDetails: { ...formData.roRoDetails, quantity: e.target.value } })}
                required={formData.isRoRo}
              />
            </div>
            <div>
              <Label>Make</Label>
              <Input
                value={formData.roRoDetails.make}
                onChange={(e) => setFormData({ ...formData, roRoDetails: { ...formData.roRoDetails, make: e.target.value } })}
                placeholder="e.g., Toyota"
              />
            </div>
            <div>
              <Label>Model</Label>
              <Input
                value={formData.roRoDetails.model}
                onChange={(e) => setFormData({ ...formData, roRoDetails: { ...formData.roRoDetails, model: e.target.value } })}
                placeholder="e.g., Camry 2020"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Running Condition</Label>
              <SearchableSelect
                options={RUNNING_CONDITION_OPTIONS}
                value={formData.roRoDetails.running}
                onChange={(value) => setFormData({ ...formData, roRoDetails: { ...formData.roRoDetails, running: value || 'yes' } })}
                placeholder="Search or select running condition"
                allowOther={false}
              />
            </div>
          </div>
        </div>
      ) : (
        /* Breakbulk Specific Fields */
        <div className="border-t pt-4">
          <h3 className="font-semibold text-lg mb-4">Cargo Details</h3>
          <div className="space-y-4">
            <div>
              <Label>Commodity</Label>
              <SearchableSelect
                options={commodityOptions}
                value={formData.commodity}
                onChange={(value) => {
                  setFormData({ ...formData, commodity: value || '' });
                  if (value) clearValidationError?.('commodity');
                }}
                placeholder="Search or select commodity"
                allowOther={false}
                error={!!validationErrors.commodity}
              />
              {validationErrors.commodity && <p className="mt-1 text-sm text-red-600">{validationErrors.commodity}</p>}
            </div>

            <div>
              <Label>Cargo Description</Label>
              <Textarea
                value={formData.cargoDescription}
                onChange={(e) => setFormData({ ...formData, cargoDescription: e.target.value })}
                placeholder="Detailed description of cargo..."
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Number of Pieces</Label>
                <Input
                  type="number"
                  value={formData.pieces}
                  onChange={(e) => setFormData({ ...formData, pieces: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Total Weight (MT)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.totalWeight}
                  onChange={(e) => setFormData({ ...formData, totalWeight: e.target.value })}
                  placeholder="Metric Tons"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Dimensions (per piece)</Label>
              <div className="grid grid-cols-4 gap-2">
                <SearchableSelect
                  options={DIMENSION_UNIT_OPTIONS}
                  className="w-24"
                  value={formData.dimensions.unit}
                  onChange={(value) => setFormData({ ...formData, dimensions: { ...formData.dimensions, unit: value || 'm' } })}
                  placeholder="Unit"
                  allowOther={false}
                />
                <Input
                  type="number"
                  step="0.01"
                  value={formData.dimensions.length}
                  onChange={(e) => setFormData({ ...formData, dimensions: { ...formData.dimensions, length: e.target.value } })}
                  placeholder="Length"
                  required
                />
                <Input
                  type="number"
                  step="0.01"
                  value={formData.dimensions.width}
                  onChange={(e) => setFormData({ ...formData, dimensions: { ...formData.dimensions, width: e.target.value } })}
                  placeholder="Width"
                  required
                />
                <Input
                  type="number"
                  step="0.01"
                  value={formData.dimensions.height}
                  onChange={(e) => setFormData({ ...formData, dimensions: { ...formData.dimensions, height: e.target.value } })}
                  placeholder="Height"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Packaging Type</Label>
              <Input
                value={formData.packaging}
                onChange={(e) => setFormData({ ...formData, packaging: e.target.value })}
                placeholder="e.g., Crated, Bare, Bundled"
              />
            </div>
          </div>
        </div>
      )}

      {/* Stowage & Special Requirements */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Stowage & Handling</h3>
        <div className="space-y-4">
          <div>
            <Label>Stowage Preference</Label>
            <SearchableSelect
              options={stowageOptions}
              value={formData.stowagePreference}
              onChange={(value) => setFormData({ ...formData, stowagePreference: value || 'on_deck' })}
              placeholder="Search or select stowage preference"
              allowOther={false}
            />
          </div>

          <div>
            <Label>Special Handling Requirements</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {specialHandlingOptions.map((option) => (
                <label key={option} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.specialHandling.includes(option)}
                    onChange={(e) => {
                      const newHandling = e.target.checked
                        ? [...formData.specialHandling, option]
                        : formData.specialHandling.filter(h => h !== option);
                      setFormData({ ...formData, specialHandling: newHandling });
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex space-x-6">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.routeSurvey}
                onChange={(e) => setFormData({ ...formData, routeSurvey: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium">Route Survey Required</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.insuranceRequired}
                onChange={(e) => setFormData({
                  ...formData,
                  insuranceRequired: e.target.checked,
                  insuranceValue: e.target.checked ? formData.insuranceValue : ''
                })}
                className="rounded"
              />
              <span className="text-sm font-medium">Marine Insurance Required</span>
            </label>
          </div>

          {formData.insuranceRequired && (
            <div className="max-w-md">
              <Label>Invoice Value (USD)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.insuranceValue}
                onChange={(e) => setFormData({ ...formData, insuranceValue: e.target.value })}
                placeholder="Enter insured invoice value"
                required={formData.insuranceRequired}
              />
            </div>
          )}
        </div>
      </div>

      {/* Photo Upload */}
      <div className="border-t pt-4">
        <Label>Cargo Photographs</Label>
        <div className="mt-2">
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors">
            <Upload size={32} className="text-gray-400 mb-2" />
            <span className="text-sm text-gray-600">
              {formData.photos.length > 0
                ? `${formData.photos.length} file(s) uploaded`
                : 'Click to upload cargo photos/documents'}
            </span>
            <span className="text-xs text-gray-500 mt-1">(JPG, PNG, PDF - Max 5 files)</span>
            <input
              type="file"
              accept="image/*,.pdf,application/pdf"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </label>
        </div>
        {formData.photos.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.photos.map((photo, index) => (
              <div key={index} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                {photo.name}
              </div>
            ))}
          </div>
        )}
      </div>

    </form>
  );
};

export default BreakbulkForm;
